import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  Divider,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const CELL = 20;
const COLS = 20;
const ROWS = 20;
const WIDTH = CELL * COLS;
const HEIGHT = CELL * ROWS;
const INITIAL_SPEED = 150;
const SPEED_INCREMENT = 5;

const DIR = { UP: "UP", DOWN: "DOWN", LEFT: "LEFT", RIGHT: "RIGHT" };

const randomCell = (snake) => {
  let cell;
  do {
    cell = {
      x: Math.floor(Math.random() * COLS),
      y: Math.floor(Math.random() * ROWS),
    };
  } while (snake.some((s) => s.x === cell.x && s.y === cell.y));
  return cell;
};

const initState = () => {
  const snake = [
    { x: 10, y: 10 },
    { x: 9, y: 10 },
    { x: 8, y: 10 },
  ];
  return {
    snake,
    food: randomCell(snake),
    dir: DIR.RIGHT,
    nextDir: DIR.RIGHT,
    score: 0,
    alive: true,
  };
};

function SnakeGame() {
  const canvasRef = useRef(null);
  const stateRef = useRef(initState());
  const loopRef = useRef(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(
    () => parseInt(localStorage.getItem("snakeHighScore") || "0")
  );
  const [running, setRunning] = useState(false);
  const [dead, setDead] = useState(false);
  const [started, setStarted] = useState(false);
  const speedRef = useRef(INITIAL_SPEED);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { snake, food } = stateRef.current;

    // Background
    ctx.fillStyle = "#0a0a1a";
    ctx.fillRect(0, 0, WIDTH, HEIGHT);

    // Grid dots
    ctx.fillStyle = "rgba(255,255,255,0.04)";
    for (let x = 0; x < COLS; x++) {
      for (let y = 0; y < ROWS; y++) {
        ctx.fillRect(x * CELL + CELL / 2 - 1, y * CELL + CELL / 2 - 1, 2, 2);
      }
    }

    // Food with glow
    const fx = food.x * CELL + CELL / 2;
    const fy = food.y * CELL + CELL / 2;
    ctx.save();
    ctx.shadowColor = "#ff4444";
    ctx.shadowBlur = 12;
    ctx.fillStyle = "#ff4444";
    ctx.beginPath();
    ctx.arc(fx, fy, CELL / 2 - 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Snake
    snake.forEach((seg, i) => {
      const x = seg.x * CELL;
      const y = seg.y * CELL;
      const ratio = 1 - i / snake.length;
      const r = Math.round(79 + ratio * (0 - 79));
      const g = Math.round(195 + ratio * (220 - 195));
      const b = Math.round(247 + ratio * (100 - 247));
      ctx.save();
      if (i === 0) {
        ctx.shadowColor = "#4FC3F7";
        ctx.shadowBlur = 10;
      }
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.beginPath();
      ctx.roundRect(x + 2, y + 2, CELL - 4, CELL - 4, 4);
      ctx.fill();
      ctx.restore();

      // Eyes on head
      if (i === 0) {
        const { dir } = stateRef.current;
        ctx.fillStyle = "#fff";
        const eyeSize = 3;
        let e1, e2;
        if (dir === DIR.RIGHT) { e1 = [x+14, y+5]; e2 = [x+14, y+12]; }
        else if (dir === DIR.LEFT) { e1 = [x+3, y+5]; e2 = [x+3, y+12]; }
        else if (dir === DIR.UP) { e1 = [x+5, y+3]; e2 = [x+12, y+3]; }
        else { e1 = [x+5, y+14]; e2 = [x+12, y+14]; }
        ctx.fillRect(e1[0], e1[1], eyeSize, eyeSize);
        ctx.fillRect(e2[0], e2[1], eyeSize, eyeSize);
      }
    });
  }, []);

  const step = useCallback(() => {
    const s = stateRef.current;
    if (!s.alive) return;

    const head = s.snake[0];
    const dir = s.nextDir;
    s.dir = dir;

    const next = { ...head };
    if (dir === DIR.RIGHT) next.x += 1;
    else if (dir === DIR.LEFT) next.x -= 1;
    else if (dir === DIR.UP) next.y -= 1;
    else next.y += 1;

    // Wall collision
    if (next.x < 0 || next.x >= COLS || next.y < 0 || next.y >= ROWS) {
      s.alive = false;
      setRunning(false);
      setDead(true);
      setHighScore((prev) => {
        const hs = Math.max(prev, s.score);
        localStorage.setItem("snakeHighScore", String(hs));
        return hs;
      });
      draw();
      return;
    }

    // Self collision (skip tail since it moves)
    const bodyCheck = s.snake.slice(0, -1);
    if (bodyCheck.some((seg) => seg.x === next.x && seg.y === next.y)) {
      s.alive = false;
      setRunning(false);
      setDead(true);
      setHighScore((prev) => {
        const hs = Math.max(prev, s.score);
        localStorage.setItem("snakeHighScore", String(hs));
        return hs;
      });
      draw();
      return;
    }

    const ateFood = next.x === s.food.x && next.y === s.food.y;
    const newSnake = [next, ...s.snake];
    if (!ateFood) newSnake.pop();
    else {
      s.score += 10;
      setScore(s.score);
      s.food = randomCell(newSnake);
      speedRef.current = Math.max(60, INITIAL_SPEED - Math.floor(s.score / 30) * SPEED_INCREMENT);
    }

    s.snake = newSnake;
    draw();
  }, [draw]);

  const startLoop = useCallback(() => {
    if (loopRef.current) clearInterval(loopRef.current);
    loopRef.current = setInterval(() => {
      step();
    }, speedRef.current);
  }, [step]);

  const stopLoop = useCallback(() => {
    if (loopRef.current) {
      clearInterval(loopRef.current);
      loopRef.current = null;
    }
  }, []);

  const handleStart = () => {
    stateRef.current = initState();
    speedRef.current = INITIAL_SPEED;
    setScore(0);
    setDead(false);
    setStarted(true);
    setRunning(true);
    draw();
    startLoop();
  };

  const handlePause = () => {
    if (running) {
      stopLoop();
      setRunning(false);
    } else {
      setRunning(true);
      startLoop();
    }
  };

  useEffect(() => {
    draw();
    return () => stopLoop();
  }, [draw, stopLoop]);

  useEffect(() => {
    if (!running) return;
    stopLoop();
    startLoop();
  }, [running, startLoop, stopLoop]);

  useEffect(() => {
    const handleKey = (e) => {
      const s = stateRef.current;
      const map = {
        ArrowUp: DIR.UP,
        ArrowDown: DIR.DOWN,
        ArrowLeft: DIR.LEFT,
        ArrowRight: DIR.RIGHT,
        w: DIR.UP,
        s: DIR.DOWN,
        a: DIR.LEFT,
        d: DIR.RIGHT,
      };
      const newDir = map[e.key];
      if (!newDir) return;
      e.preventDefault();

      const opp = {
        [DIR.UP]: DIR.DOWN,
        [DIR.DOWN]: DIR.UP,
        [DIR.LEFT]: DIR.RIGHT,
        [DIR.RIGHT]: DIR.LEFT,
      };
      if (newDir !== opp[s.dir]) s.nextDir = newDir;
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const MobileBtn = ({ label, dir }) => (
    <Button
      variant="outlined"
      size="small"
      onPointerDown={() => {
        const s = stateRef.current;
        const opp = {
          [DIR.UP]: DIR.DOWN,
          [DIR.DOWN]: DIR.UP,
          [DIR.LEFT]: DIR.RIGHT,
          [DIR.RIGHT]: DIR.LEFT,
        };
        if (dir !== opp[s.dir]) s.nextDir = dir;
      }}
      sx={{
        minWidth: 48,
        height: 48,
        borderColor: "rgba(79,195,247,0.4)",
        color: "#4FC3F7",
        fontSize: "1.2rem",
        "&:hover": { borderColor: "#4FC3F7", bgcolor: "rgba(79,195,247,0.1)" },
      }}
    >
      {label}
    </Button>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
      {/* Score bar */}
      <Box sx={{ display: "flex", gap: 3, mb: 0.5 }}>
        <Chip
          icon={<SportsEsportsIcon sx={{ fontSize: 16 }} />}
          label={`Score: ${score}`}
          sx={{ bgcolor: "rgba(79,195,247,0.15)", color: "#4FC3F7", fontWeight: 700 }}
        />
        <Chip
          icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />}
          label={`Best: ${highScore}`}
          sx={{ bgcolor: "rgba(255,193,7,0.15)", color: "#FFC107", fontWeight: 700 }}
        />
      </Box>

      {/* Canvas */}
      <Box
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          border: "2px solid rgba(79,195,247,0.3)",
          boxShadow: "0 0 24px rgba(79,195,247,0.15)",
        }}
      >
        <canvas ref={canvasRef} width={WIDTH} height={HEIGHT} style={{ display: "block" }} />

        {/* Overlay when not started or dead */}
        {(!started || dead) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(10,10,26,0.85)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            {dead && (
              <>
                <Typography variant="h5" sx={{ color: "#ff4444", fontWeight: 700 }}>
                  Game Over!
                </Typography>
                <Typography sx={{ color: "#fff" }}>Score: {score}</Typography>
              </>
            )}
            {!started && !dead && (
              <Typography variant="h6" sx={{ color: "#4FC3F7", fontWeight: 600 }}>
                Snake Game
              </Typography>
            )}
            <Button
              variant="contained"
              startIcon={dead ? <RefreshIcon /> : <PlayArrowIcon />}
              onClick={handleStart}
              sx={{
                background: "linear-gradient(135deg, #4FC3F7 0%, #0288D1 100%)",
                fontWeight: 700,
                px: 3,
              }}
            >
              {dead ? "Play Again" : "Start Game"}
            </Button>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
              Use Arrow Keys or WASD
            </Typography>
          </Box>
        )}
      </Box>

      {/* Controls */}
      {started && !dead && (
        <Button
          variant="outlined"
          size="small"
          startIcon={running ? <PauseIcon /> : <PlayArrowIcon />}
          onClick={handlePause}
          sx={{
            borderColor: "rgba(79,195,247,0.4)",
            color: "#4FC3F7",
            "&:hover": { borderColor: "#4FC3F7", bgcolor: "rgba(79,195,247,0.1)" },
          }}
        >
          {running ? "Pause" : "Resume"}
        </Button>
      )}

      {/* Mobile D-pad */}
      <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", alignItems: "center", gap: 0.5, mt: 0.5 }}>
        <MobileBtn label="▲" dir={DIR.UP} />
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <MobileBtn label="◄" dir={DIR.LEFT} />
          <MobileBtn label="▼" dir={DIR.DOWN} />
          <MobileBtn label="►" dir={DIR.RIGHT} />
        </Box>
      </Box>

      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.35)", display: { xs: "none", sm: "block" } }}>
        Arrow Keys or WASD to move · Eat the red dot · Don't hit the walls!
      </Typography>
    </Box>
  );
}

export default function DevModePage() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0a0a1a 0%, #0d1b2a 50%, #0a1628 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 6,
      }}
    >
      {/* Back button */}
      <Box sx={{ position: "fixed", top: 20, left: 20 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/login")}
          sx={{
            color: "rgba(255,255,255,0.6)",
            textTransform: "none",
            "&:hover": { color: "#fff" },
          }}
        >
          Back to Login
        </Button>
      </Box>

      {/* Header section */}
      <Box sx={{ textAlign: "center", mb: 5, maxWidth: 560 }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #4FC3F7 0%, #0288D1 100%)",
            boxShadow: "0 0 32px rgba(79,195,247,0.4)",
            mb: 3,
            animation: "pulse 2s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { boxShadow: "0 0 32px rgba(79,195,247,0.4)" },
              "50%": { boxShadow: "0 0 48px rgba(79,195,247,0.7)" },
            },
          }}
        >
          <BuildIcon sx={{ fontSize: 40, color: "#fff" }} />
        </Box>

        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            color: "#fff",
            mb: 1.5,
            fontSize: { xs: "1.8rem", sm: "2.5rem" },
          }}
        >
          We're Building Something Great
        </Typography>

        <Typography
          variant="h6"
          sx={{
            color: "rgba(255,255,255,0.6)",
            fontWeight: 400,
            mb: 2,
            fontSize: { xs: "1rem", sm: "1.1rem" },
          }}
        >
          The system is currently undergoing scheduled maintenance & upgrades.
          Our team is working hard to bring you an improved experience.
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
          {["New Features", "Bug Fixes", "Performance Improvements"].map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              sx={{
                bgcolor: "rgba(79,195,247,0.15)",
                color: "#4FC3F7",
                border: "1px solid rgba(79,195,247,0.3)",
                fontWeight: 500,
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Game section */}
      <Paper
        elevation={0}
        sx={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 3,
          p: { xs: 2, sm: 3 },
          maxWidth: 480,
          width: "100%",
          backdropFilter: "blur(10px)",
        }}
      >
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography
            variant="h6"
            sx={{ color: "#fff", fontWeight: 700, mb: 0.5 }}
          >
            Kill Some Time 🎮
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
            Play Snake while you wait for us to finish up
          </Typography>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 2.5 }} />

        <SnakeGame />
      </Paper>

      {/* Footer note */}
      <Typography
        variant="body2"
        sx={{ color: "rgba(255,255,255,0.35)", mt: 5, textAlign: "center" }}
      >
        We apologize for the inconvenience. Thank you for your patience.
        <br />
        © {new Date().getFullYear()} Sababisha Solutions Ltd
      </Typography>
    </Box>
  );
}
